/**
 * IntegrationsCard Component (Story 3.8, AC #2; Story 4.11, Task 5; Story 4.14, Task 5; Story 4.15, Task 2)
 *
 * Displays connected integrations with status indicators.
 * PageSpeed integrations show condensed scores "M: 72 | D: 92" with color coding.
 * Story 4.14: Fetches health data from server and shows HealthStatusPopover for warning/error states.
 * Story 4.15: Quick Assign from existing connections + per-row disconnect + last-connection prompt.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Button,
} from "@provenleap/ui";
import { motion, AnimatePresence } from "motion/react";
import {
  CheckCircle,
  Check,
  CircleNotch,
  MagnifyingGlass,
  X,
  SquaresFour,
  ChartBar,
  Megaphone,
  ShareNetwork,
  MagnifyingGlassPlus,
  ShoppingCart,
  DotsThreeCircle,
  Funnel,
  CaretDown,
  DotsNine,
  Broom,
  Percent,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useIntegrationConnect } from "../../hooks/useIntegrationConnect";
import { PropertySelector } from "../integrations/PropertySelector";
import { PageSpeedDetailModal } from "../integrations/PageSpeedDetailModal";
import type { PageSpeedMetricRecord } from "../integrations/PageSpeedDetailModal";
import { ReauthorizeDialog } from "../integrations/ReauthorizeDialog";
import { AssignIntegrationsModal } from "../integrations/AssignIntegrationsModal";
import type { TenantGoogleConnection } from "../integrations/AssignIntegrationsModal";
import { DisconnectIntegrationDialog } from "../integrations/DisconnectIntegrationDialog";
import type { DisconnectResult } from "../integrations/DisconnectIntegrationDialog";
import { CleanupStaleResourcesDialog } from "../integrations/CleanupStaleResourcesDialog";
import type { StaleGrantResource } from "../integrations/CleanupStaleResourcesDialog";
import { MarkupSettingsDialog } from "./MarkupSettingsDialog";
import { DisconnectDialog } from "../integrations/DisconnectDialog";
import {
  DiscardIncompleteSetupDialog,
  type DiscardIncompleteSetupTarget,
} from "../integrations/DiscardIncompleteSetupDialog";
import {
  ConnectionsTree,
  connectionsPlatformSectionId,
} from "../integrations/ConnectionsTree";
import { API_KEY_PLATFORMS, getScopesForPlatforms } from "@provenleap/integrations";
import { apiGet, apiPost, apiDelete } from "../../lib/apiClient";
import {
  openOAuthPopup,
  fetchIntegrationAuthUrl,
  isMobileDevice,
  OAuthPopupBlockedError,
} from "../../lib/oauth-popup";
import { useAuth } from "../../stores/authStore";
import { PICKER_PLATFORMS, logoUrl } from "../../lib/integrations";
import type { ToastType } from "../../hooks/useToast";
import type { ClientIntegration, IntegrationHealthResult } from "./types";

/** Map platform ID to its display name */
const PLATFORM_DISPLAY_NAME = new Map(
  PICKER_PLATFORMS.map((p) => [p.platform, p.displayName])
);

/** Landing-page-style category groupings (matches public IntegrationsSection). */
type LandingCategoryKey =
  | "all"
  | "analytics"
  | "advertising"
  | "social"
  | "seo"
  | "commerce"
  | "other";

/** Map PICKER_PLATFORMS category → landing-page category. */
function pickerCategoryToLanding(
  pickerCategory: string,
  platform: string
): Exclude<LandingCategoryKey, "all"> {
  // Special-case Google sub-platforms (the picker lumps them all under "Google")
  if (pickerCategory === "Google") {
    if (platform.endsWith("-ads")) return "advertising";
    return "analytics";
  }
  if (pickerCategory === "Advertising") return "advertising";
  if (pickerCategory === "Social Media") return "social";
  if (pickerCategory === "SEO") return "seo";
  if (pickerCategory === "Email & Commerce") return "commerce";
  return "other";
}

const LANDING_FILTERS: { key: LandingCategoryKey; label: string; icon: Icon }[] = [
  { key: "all", label: "All categories", icon: SquaresFour },
  { key: "analytics", label: "Analytics", icon: ChartBar },
  { key: "advertising", label: "Advertising", icon: Megaphone },
  { key: "social", label: "Social", icon: ShareNetwork },
  { key: "seo", label: "SEO", icon: MagnifyingGlassPlus },
  { key: "commerce", label: "Commerce", icon: ShoppingCart },
  { key: "other", label: "Other", icon: DotsThreeCircle },
];

interface IntegrationsCardProps {
  clientId: string;
  clientName?: string;
  integrations?: ClientIntegration[];
  showToast: (type: ToastType, message: string) => void;
  /**
   * Story 32-3 (AC #11): when the OAuth redirect flow landed with
   * `catalogCount > 1`, the client page passes the connected platform here —
   * the card opens its CatalogPickerDialog once on mount so the user can
   * attach the additional discovered resources.
   */
  initialCatalogPlatform?: string | null;
  /**
   * Story 32-3 (review R1-F3): fired once the initial picker open was
   * consumed — the page clears its pending state so a tab-switch remount
   * never re-opens the picker.
   */
  onInitialCatalogHandled?: () => void;
}

/** Color class for a performance score */
function scoreColor(score: number | null | undefined): string {
  if (score == null) return "text-muted-foreground";
  if (score >= 90) return "text-green-600";
  if (score >= 50) return "text-orange-500";
  return "text-red-600";
}

/** Condensed PageSpeed scores badge: "M: 72 | D: 92" */
function PageSpeedScoreBadge({
  metrics,
  onClick,
}: {
  metrics: PageSpeedMetricRecord[] | undefined;
  onClick: () => void;
}) {
  const mobile = metrics?.find(
    (r) => r._reportType === "lighthouse" && r.strategy === "mobile"
  );
  const desktop = metrics?.find(
    (r) => r._reportType === "lighthouse" && r.strategy === "desktop"
  );

  if (!mobile && !desktop) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-green-50 text-green-800 border border-green-200">
        <CheckCircle size={12} />
        Connected
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium bg-card border border-input hover:bg-muted/30 transition-colors cursor-pointer"
      title="View PageSpeed details"
    >
      {mobile?.performanceScore != null && (
        <span className={`font-semibold tabular-nums ${scoreColor(mobile.performanceScore)}`}>
          M: {mobile.performanceScore}
        </span>
      )}
      {mobile?.performanceScore != null && desktop?.performanceScore != null && (
        <span className="text-muted-foreground">|</span>
      )}
      {desktop?.performanceScore != null && (
        <span className={`font-semibold tabular-nums ${scoreColor(desktop.performanceScore)}`}>
          D: {desktop.performanceScore}
        </span>
      )}
    </button>
  );
}

/** Platforms that require property/site selection after OAuth (excludes API-key platforms) */
const PLATFORMS_NEEDING_PROPERTY = new Set([
  "google-analytics-4",
  "google-search-console",
  "google-ads",
  "youtube-analytics",
  "google-business-profile",
]);

/** Platform-specific PropertySelector config */
const PROPERTY_SELECTOR_CONFIG: Record<
  string,
  { title: string; description: string; emptyMessage: string; idPrefix: string }
> = {
  "google-analytics-4": {
    title: "Select GA4 Property",
    description:
      "Choose the Google Analytics 4 property you want to connect to this client.",
    emptyMessage:
      "No GA4 properties found. Ensure Google Analytics is set up for your account.",
    idPrefix: "ga4-property",
  },
  "google-search-console": {
    title: "Select Search Console Property",
    description:
      "Choose the Search Console property you want to connect to this client.",
    emptyMessage:
      "No Search Console properties found. Ensure you have verified sites in Google Search Console.",
    idPrefix: "gsc-property",
  },
  "google-ads": {
    title: "Select Google Ads Account",
    description:
      "Choose which Google Ads account to use for this client.",
    emptyMessage:
      "No Google Ads accounts found. Ensure your Google account has access to at least one Google Ads account.",
    idPrefix: "google-ads-account",
  },
  "youtube-analytics": {
    title: "Select YouTube Channel",
    description:
      "Choose the YouTube channel to connect to this client.",
    emptyMessage:
      "No YouTube channels found. Ensure your Google account owns or manages at least one YouTube channel.",
    idPrefix: "youtube-channel",
  },
  "google-business-profile": {
    title: "Select Business Location",
    description:
      "Choose which Google Business Profile location to track.",
    emptyMessage:
      "No business locations found. Ensure GBP access is granted.",
    idPrefix: "",
  },
};

/**
 * Derive a fallback IntegrationHealthResult from a ClientIntegration prop.
 * Used as immediate visual feedback while the server-side health API loads.
 */
function deriveFallbackHealth(integration: ClientIntegration): IntegrationHealthResult {
  const failures = integration.consecutiveFailures ?? 0;
  const ts = integration.tokenStatus;

  let status: IntegrationHealthResult["status"] = "healthy";
  const issues: string[] = [];

  // Error conditions (same logic as server-side)
  if (
    ts === "expired" ||
    ts === "revoked" ||
    ts === "refresh_failed" ||
    failures >= 5
  ) {
    status = "error";
    if (ts === "expired") issues.push("Token expired");
    if (ts === "revoked") issues.push("Access revoked");
    if (ts === "refresh_failed") issues.push("Token refresh failed");
    if (failures >= 5) issues.push(`${failures} consecutive sync failures`);
  } else if (ts === "expiring_soon" || failures >= 2) {
    status = "warning";
    if (ts === "expiring_soon") issues.push("Token expiring soon");
    if (failures >= 2) issues.push(`${failures} recent sync failures`);
  }

  return {
    integrationId: integration.id,
    platform: integration.type,
    accountName: integration.accountName,
    status,
    issues,
    recommendedAction: status === "error" ? "Re-authorize this integration to restore data access" : null,
    lastSyncAt: null,
    syncStatus: integration.syncStatus,
    tokenStatus: ts,
  };
}

/** PageSpeed metrics API response shape */
interface PageSpeedMetricsResponse {
  data: {
    integrationId: string;
    url: string;
    hostname: string | null;
    metrics: PageSpeedMetricRecord[];
    lastSyncAt: string | null;
    syncStatus: string;
  };
}

export function IntegrationsCard({
  clientId,
  clientName,
  integrations: integrationsProp,
  showToast,
  initialCatalogPlatform,
  onInitialCatalogHandled,
}: IntegrationsCardProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userRole = user?.role ?? "viewer";
  const canManage = userRole === "owner" || userRole === "admin";
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [propertySelectorIntegrationId, setPropertySelectorIntegrationId] =
    useState<string | null>(null);
  const [propertySelectorPlatform, setPropertySelectorPlatform] =
    useState<string | null>(null);
  // Queue for sequential property selection after quick-assign (Story 4.15)
  const [propertySelectionQueue, setPropertySelectionQueue] = useState<
    Array<{ integrationId: string; platform: string }>
  >([]);

  // Re-authorize dialog state (Story 4.14, Task 7)
  const [reauthorizeIntegration, setReauthorizeIntegration] = useState<{
    id: string;
    platform: string;
    accountName: string | null;
  } | null>(null);
  const [reauthorizeLoading, setReauthorizeLoading] = useState(false);

  // Disconnect dialog state (Story 4.15, Task 3)
  const [disconnectTarget, setDisconnectTarget] = useState<{
    id: string;
    platform: string;
    accountName: string | null;
  } | null>(null);
  const [discardIncompleteSetupTarget, setDiscardIncompleteSetupTarget] =
    useState<DiscardIncompleteSetupTarget | null>(null);
  const [
    lastDiscardIncompleteSetupTarget,
    setLastDiscardIncompleteSetupTarget,
  ] = useState<DiscardIncompleteSetupTarget | null>(null);
  const [discardingIncompleteSetup, setDiscardingIncompleteSetup] =
    useState(false);
  const discardingIncompleteSetupRef = useRef(false);
  const discardIncompleteSetupClientIdRef = useRef(clientId);
  useEffect(() => {
    if (discardIncompleteSetupClientIdRef.current === clientId) return;
    discardIncompleteSetupClientIdRef.current = clientId;
    setDiscardIncompleteSetupTarget(null);
    setLastDiscardIncompleteSetupTarget(null);
  }, [clientId]);
  useEffect(() => {
    if (discardIncompleteSetupTarget) {
      if (discardIncompleteSetupTarget.clientId === clientId) {
        setLastDiscardIncompleteSetupTarget(discardIncompleteSetupTarget);
      }
      return;
    }
    if (!lastDiscardIncompleteSetupTarget) return;
    const timeout = setTimeout(
      () => setLastDiscardIncompleteSetupTarget(null),
      300,
    );
    return () => clearTimeout(timeout);
  }, [clientId, discardIncompleteSetupTarget, lastDiscardIncompleteSetupTarget]);
  // Stale-resource cleanup dialog state (Story 32.8)
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
  // Per-client markup settings dialog state (Story 17.2)
  const [markupDialogOpen, setMarkupDialogOpen] = useState(false);
  // Last-connection dialog state (chained from disconnect)
  const [lastConnectionDialog, setLastConnectionDialog] = useState<{
    oauthTokenId: string;
    platformName: string;
    affectedServices: string[];
  } | null>(null);
  const [lastConnectionLoading, setLastConnectionLoading] = useState(false);

  // PageSpeed detail modal state
  const [pageSpeedDetailOpen, setPageSpeedDetailOpen] = useState(false);
  const [pageSpeedDetailIntegrationId, setPageSpeedDetailIntegrationId] =
    useState<string | null>(null);

  // PageSpeed metrics cache: integrationId -> metrics data
  const [pageSpeedMetrics, setPageSpeedMetrics] = useState<
    Map<string, { url: string; hostname: string | null; metrics: PageSpeedMetricRecord[]; lastSyncAt: string | null }>
  >(new Map());

  // Top search (filters tile grid by name) + filter popover (selects categories)
  const [search, setSearch] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterSearch, setFilterSearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<Set<Exclude<LandingCategoryKey, "all">>>(
    new Set(),
  );
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [density, setDensity] = useState<"compact" | "comfortable">(() => {
    if (typeof window === "undefined") return "comfortable";
    return (localStorage.getItem("integrationsGridDensity") as "compact" | "comfortable") ?? "comfortable";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("integrationsGridDensity", density);
    }
  }, [density]);

  // Fetch integrations from server (falls back to prop if provided)
  const { data: fetchedIntegrations } = useQuery<ClientIntegration[]>({
    queryKey: ["client-integrations", clientId],
    queryFn: async () => {
      const res = await apiGet(`/api/clients/${clientId}/integrations`);
      if (!res.ok) return [];
      const json = (await res.json()) as { data: ClientIntegration[] };
      return json.data;
    },
  });

  const integrations = fetchedIntegrations ?? integrationsProp;
  const hasIntegrations = integrations && integrations.length > 0;

  // Fetch health data from server (Story 4.14, Task 5.1)
  const { data: healthData } = useQuery<IntegrationHealthResult[]>({
    queryKey: ["client-integrations-health", clientId],
    queryFn: async () => {
      const res = await apiGet(`/api/clients/${clientId}/integrations/health`);
      if (!res.ok) return [];
      const json = (await res.json()) as { data: IntegrationHealthResult[] };
      return json.data;
    },
    enabled: hasIntegrations,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  // Fetch tenant Google connections for "Use Existing" feature (Story 4.15)
  const { data: tenantConnections } = useQuery<TenantGoogleConnection[]>({
    queryKey: ["tenant-google-connections"],
    queryFn: async () => {
      const res = await apiGet("/api/integrations/google/connections");
      if (!res.ok) return [];
      const json = (await res.json()) as { data: TenantGoogleConnection[] };
      return json.data;
    },
    staleTime: 60_000,
  });

  const hasTenantConnections = (tenantConnections?.length ?? 0) > 0;

  // Build a map of integrationId -> health result for quick lookup.
  // Currently unused in the visual grid but kept hot for re-introduction.
  const healthMap = useMemo(() => {
    const map = new Map<string, IntegrationHealthResult>();
    if (Array.isArray(healthData)) {
      for (const h of healthData) {
        map.set(h.integrationId, h);
      }
    }
    return map;
  }, [healthData]);
  void healthMap;

  // Find connected PageSpeed integrations
  const pageSpeedIntegrations = useMemo(
    () => (integrations ?? []).filter((i) => i.type === "google-pagespeed"),
    [integrations]
  );

  // Fetch PageSpeed metrics for connected PageSpeed integrations
  useEffect(() => {
    if (pageSpeedIntegrations.length === 0) return;

    let cancelled = false;
    for (const psi of pageSpeedIntegrations) {
      apiGet(`/api/integrations/${psi.id}/pagespeed/metrics`)
        .then((res) => {
          if (!res.ok || cancelled) return;
          return res.json();
        })
        .then((json) => {
          if (cancelled || !json) return;
          const data = (json as PageSpeedMetricsResponse).data;
          setPageSpeedMetrics((prev) => {
            const next = new Map(prev);
            next.set(psi.id, {
              url: data.url,
              hostname: data.hostname,
              metrics: data.metrics ?? [],
              lastSyncAt: data.lastSyncAt,
            });
            return next;
          });
        })
        .catch(() => {
          // Non-fatal — card still renders with generic badge
        });
    }
    return () => { cancelled = true; };
  }, [pageSpeedIntegrations]);

  // Current PageSpeed detail data
  const currentPageSpeedDetail = pageSpeedDetailIntegrationId
    ? pageSpeedMetrics.get(pageSpeedDetailIntegrationId)
    : null;

  /**
   * After OAuth completes, invalidate the integrations query so the card
   * re-fetches and shows the newly connected integration. Also check if
   * the new integration needs property/site selection.
   */
  const handleIntegrationChange = useCallback(async () => {
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ["client-integrations", clientId] }),
      queryClient.invalidateQueries({ queryKey: ["client-integrations-health", clientId] }),
      queryClient.invalidateQueries({ queryKey: ["client-connections", clientId] }),
    ]);
    // After refetch completes, check for integrations needing property selection
    const cached = queryClient.getQueryData<ClientIntegration[]>(["client-integrations", clientId]);
    if (cached) {
      const needsProperty = cached.find(
        (i) =>
          PLATFORMS_NEEDING_PROPERTY.has(i.type) &&
          !(API_KEY_PLATFORMS as ReadonlySet<string>).has(i.type) &&
          !i.accountName &&
          i.syncStatus === "pending"
      );
      if (needsProperty) {
        setPropertySelectorIntegrationId(needsProperty.id);
        setPropertySelectorPlatform(needsProperty.type);
      }
    }
  }, [clientId, queryClient]);

  // One-click connect: hook-managed flow per platform (replaces the picker
  // modal). Declared BEFORE handleReauthorizeConfirm so the Story 32-3
  // popup-flow arm can reference openCatalogPicker.
  const { connectingPlatform, connect, openCatalogPicker, dialogs: connectDialogs } = useIntegrationConnect({
    clientId,
    connectedIntegrations: integrations ?? [],
    showToast,
    onIntegrationChange: handleIntegrationChange,
  });

  // Story 32-3 (AC #11), redirect-flow arm: one-shot picker open when the
  // page landed from an OAuth success with catalogCount > 1. The handled
  // callback clears the page's pending state (review R1-F3: the ref alone
  // resets on tab-switch remount; the page state must too).
  const initialCatalogOpened = useRef(false);
  useEffect(() => {
    if (!initialCatalogPlatform || initialCatalogOpened.current) return;
    initialCatalogOpened.current = true;
    openCatalogPicker(initialCatalogPlatform);
    onInitialCatalogHandled?.();
  }, [initialCatalogPlatform, openCatalogPicker, onInitialCatalogHandled]);

  /**
   * After quick-assign completes, refetch integrations and queue property selection
   * for each newly created integration that needs it (Story 4.15, Task 2.3).
   */
  const handleAssigned = useCallback(async (createdPlatforms: string[]) => {
    // Refetch integrations to get the new IDs
    await queryClient.refetchQueries({ queryKey: ["client-integrations", clientId] });
    await queryClient.invalidateQueries({ queryKey: ["tenant-google-connections"] });
    await queryClient.invalidateQueries({ queryKey: ["client-connections", clientId] });

    const cached = queryClient.getQueryData<ClientIntegration[]>(["client-integrations", clientId]);
    if (!cached) return;

    // Find newly created integrations needing property selection
    // (google-pagespeed uses URL, not property — skip it)
    const needsProperty = cached.filter(
      (i) =>
        createdPlatforms.includes(i.type) &&
        PLATFORMS_NEEDING_PROPERTY.has(i.type) &&
        !i.accountName &&
        i.syncStatus === "pending"
    );

    if (needsProperty.length > 0) {
      // Queue property selection for each
      const queue = needsProperty.map((i) => ({
        integrationId: i.id,
        platform: i.type,
      }));
      setPropertySelectionQueue(queue);
      // Start with the first one
      setPropertySelectorIntegrationId(queue[0]!.integrationId);
      setPropertySelectorPlatform(queue[0]!.platform);
    }
  }, [clientId, queryClient]);

  /**
   * After a property/site/account is picked for an integration, the server
   * row gains account_id/account_name — refresh everything that displays it
   * (Story 32.1 review R2: without this the ConnectionsTree visibly lingers
   * on "Pending setup" after selection).
   */
  const refreshAfterPropertySelected = useCallback(() => {
    void Promise.all([
      queryClient.invalidateQueries({ queryKey: ["client-integrations", clientId] }),
      queryClient.invalidateQueries({ queryKey: ["client-integrations-health", clientId] }),
      queryClient.invalidateQueries({ queryKey: ["client-connections", clientId] }),
    ]);
  }, [clientId, queryClient]);

  /**
   * Process next item in property selection queue (Story 4.15, Task 2.3).
   */
  const handlePropertySelectedFromQueue = useCallback(() => {
    setPropertySelectorIntegrationId(null);
    setPropertySelectorPlatform(null);

    setPropertySelectionQueue((prev) => {
      const remaining = prev.slice(1);
      if (remaining.length > 0) {
        // Open next one after a brief delay for smooth UX
        const next = remaining[0]!;
        setTimeout(() => {
          setPropertySelectorIntegrationId(next.integrationId);
          setPropertySelectorPlatform(next.platform);
        }, 300);
      }
      return remaining;
    });
  }, []);

  /**
   * Re-authorize flow (Story 4.14, Task 7):
   * 1. Open OAuth popup for the integration's platform
   * 2. On success: call reset-health endpoint
   * 3. Invalidate caches and show toast
   */
  const handleReauthorizeConfirm = useCallback(async () => {
    if (!reauthorizeIntegration) return;
    setReauthorizeLoading(true);
    try {
      const scopes = getScopesForPlatforms([reauthorizeIntegration.platform]);
      const authUrlPromise = fetchIntegrationAuthUrl(clientId, scopes);

      if (isMobileDevice()) {
        const url = await authUrlPromise;
        window.location.href = url;
        return;
      }

      const { promise } = openOAuthPopup({
        authUrl: authUrlPromise,
        appUrl: window.location.origin,
      });

      const result = await promise;

      // Reset health after successful OAuth
      await apiPost(`/api/integrations/${reauthorizeIntegration.id}/reset-health`);

      // Story 32-3 (AC #11), popup-flow arm: a re-auth that cataloged more
      // than one resource offers the picker for the additional accounts.
      if ((result.catalogCount ?? 0) > 1) {
        openCatalogPicker(reauthorizeIntegration.platform);
      }

      // Invalidate all relevant caches (Story 4.19: include notification queries)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["client-integrations", clientId] }),
        queryClient.invalidateQueries({ queryKey: ["client-integrations-health", clientId] }),
        queryClient.invalidateQueries({ queryKey: ["client-connections", clientId] }),
        queryClient.invalidateQueries({ queryKey: ["integration-alerts"] }),
        queryClient.invalidateQueries({ queryKey: ["notification-unread-count"] }),
        queryClient.invalidateQueries({ queryKey: ["notification-list"] }),
      ]);

      showToast("success", "Integration re-authorized and notifications cleared");
    } catch (err) {
      if (err instanceof OAuthPopupBlockedError) {
        showToast("error", "Popup was blocked. Please allow popups and try again.");
      } else {
        showToast("error", "Re-authorization failed. Please try again.");
      }
    } finally {
      setReauthorizeLoading(false);
      setReauthorizeIntegration(null);
    }
  }, [reauthorizeIntegration, clientId, queryClient, showToast, openCatalogPicker]);

  const invalidateAfterIncompleteSetupDiscard = useCallback(() => {
    const queryKeys = [
      ["client-connections", clientId],
      ["client-integrations", clientId],
      ["client-integrations-health", clientId],
      ["tenant-google-connections"],
      ["integration-alerts"],
      ["notification-unread-count"],
      ["notification-list"],
      ["grant-catalog"],
    ];
    void Promise.allSettled(
      queryKeys.map(async (queryKey) => {
        await queryClient.invalidateQueries({ queryKey });
      }),
    );
  }, [clientId, queryClient]);

  const handleDiscardIncompleteSetup = useCallback(async () => {
    const target = discardIncompleteSetupTarget;
    if (
      !canManage ||
      !target ||
      target.clientId !== clientId ||
      discardingIncompleteSetupRef.current
    ) {
      return;
    }

    discardingIncompleteSetupRef.current = true;
    setDiscardingIncompleteSetup(true);
    try {
      const response = await apiDelete(
        `/api/integrations/incomplete-setups/${encodeURIComponent(target.integrationId)}`,
        {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clientId: target.clientId,
            platform: target.platform,
            oauthTokenId: target.oauthTokenId,
            oauthGrantResourceId: target.oauthGrantResourceId,
          }),
        },
      );

      if (!response.ok) {
        if (response.status === 404 || response.status === 409) {
          setDiscardIncompleteSetupTarget(null);
          showToast(
            "info",
            "This incomplete setup is no longer available. Connections have been refreshed.",
          );
          invalidateAfterIncompleteSetupDiscard();
          return;
        }

        showToast(
          "error",
          "Could not remove the incomplete setup. Please try again.",
        );
        return;
      }

      setDiscardIncompleteSetupTarget(null);
      showToast(
        "success",
        `Incomplete setup removed. Connect ${target.displayName} again to continue.`,
      );
      invalidateAfterIncompleteSetupDiscard();
    } catch {
      showToast(
        "error",
        "Could not remove the incomplete setup. Please try again.",
      );
    } finally {
      discardingIncompleteSetupRef.current = false;
      setDiscardingIncompleteSetup(false);
    }
  }, [
    canManage,
    clientId,
    discardIncompleteSetupTarget,
    invalidateAfterIncompleteSetupDiscard,
    showToast,
  ]);

  /**
   * After disconnect, invalidate relevant queries and handle last-connection flow (Story 4.15, Task 3.2-3.4).
   */
  const handleDisconnected = useCallback(
    async (result: DisconnectResult) => {
      // Auto-dismiss notifications for the disconnected token (Story 4.19, AC #8)
      if (result.oauthTokenId) {
        await apiPost(`/api/notifications/dismiss-by-token/${result.oauthTokenId}`).catch(() => {
          // Non-fatal — notifications will remain but are harmless
        });
      }

      // Invalidate all relevant caches (Task 3.4, Story 4.19: include notification queries)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["client-integrations", clientId] }),
        queryClient.invalidateQueries({ queryKey: ["client-integrations-health", clientId] }),
        queryClient.invalidateQueries({ queryKey: ["client-connections", clientId] }),
        queryClient.invalidateQueries({ queryKey: ["integration-alerts"] }),
        queryClient.invalidateQueries({ queryKey: ["tenant-google-connections"] }),
        queryClient.invalidateQueries({ queryKey: ["notification-unread-count"] }),
        queryClient.invalidateQueries({ queryKey: ["notification-list"] }),
      ]);

      // If this was the last connection using this OAuth token, show the keep/revoke dialog
      // Only for providers that support token revocation via API (currently Google only).
      // Other providers (Mailchimp, TikTok, etc.) have permanent tokens or require
      // revocation through their own dashboard — no keep/revoke choice needed.
      if (result.lastConnection && result.oauthTokenId) {
        const platform = disconnectTarget?.platform ?? "";
        const isGooglePlatform = platform.startsWith("google-") || platform === "youtube-analytics";
        if (isGooglePlatform) {
          const platformName = disconnectTarget
            ? PLATFORM_DISPLAY_NAME.get(disconnectTarget.platform) ?? disconnectTarget.platform
            : "";
          setLastConnectionDialog({
            oauthTokenId: result.oauthTokenId,
            platformName,
            affectedServices: [],
          });
        }
        // Non-Google providers: token stays in DB (harmless) or can be cleaned up by admin
      }

      setDisconnectTarget(null);
    },
    [clientId, queryClient, disconnectTarget]
  );

  /**
   * Last-connection: keep the OAuth connection (no further action needed).
   */
  const handleKeepAuth = useCallback(() => {
    setLastConnectionDialog(null);
  }, []);

  /**
   * Last-connection: revoke the OAuth token at Google.
   */
  const handleRevokeAll = useCallback(async () => {
    if (!lastConnectionDialog) return;
    setLastConnectionLoading(true);

    try {
      const res = await apiDelete("/api/integrations/google/revoke", {
        body: JSON.stringify({ oauthTokenId: lastConnectionDialog.oauthTokenId }),
      });

      if (!res.ok) {
        const errorJson = await res.json().catch(() => null);
        const detail =
          (errorJson as { detail?: string })?.detail ?? "Failed to revoke Google access";
        showToast("error", detail);
        return;
      }

      showToast("success", "Google account access revoked");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["client-integrations", clientId] }),
        queryClient.invalidateQueries({ queryKey: ["client-integrations-health", clientId] }),
        queryClient.invalidateQueries({ queryKey: ["client-connections", clientId] }),
        queryClient.invalidateQueries({ queryKey: ["integration-alerts"] }),
        queryClient.invalidateQueries({ queryKey: ["tenant-google-connections"] }),
      ]);
    } catch {
      showToast("error", "Failed to revoke Google access. Please try again.");
    } finally {
      setLastConnectionLoading(false);
      setLastConnectionDialog(null);
    }
  }, [lastConnectionDialog, queryClient, showToast]);

  // Connected lookup
  const connectedPlatformTypes = useMemo(
    () => new Set((integrations ?? []).map((i) => i.type)),
    [integrations]
  );

  // All platforms enriched with landing-category mapping
  const allPlatformsWithCat = useMemo(() => {
    return PICKER_PLATFORMS.map((p) => ({
      ...p,
      landingCat: pickerCategoryToLanding(p.category, p.platform),
    }));
  }, []);

  // Filter by active landing category
  const visiblePlatforms = useMemo(() => {
    const q = search.trim().toLowerCase();
    const noCategoryFilter = selectedCategories.size === 0;
    return allPlatformsWithCat.filter((p) => {
      if (showActiveOnly && !connectedPlatformTypes.has(p.platform)) return false;
      if (!noCategoryFilter && !selectedCategories.has(p.landingCat)) return false;
      if (!q) return true;
      return (
        p.displayName.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q)
      );
    });
  }, [allPlatformsWithCat, selectedCategories, search, showActiveOnly, connectedPlatformTypes]);

  function toggleCategory(cat: Exclude<LandingCategoryKey, "all">) {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }

  // Filter pop-over: only category options that have at least one matching platform name
  const visibleCategoryOptions = useMemo(() => {
    const q = filterSearch.trim().toLowerCase();
    return LANDING_FILTERS.filter((f) => f.key !== "all").filter(
      (f) => !q || f.label.toLowerCase().includes(q),
    );
  }, [filterSearch]);

  const totalKnown = PICKER_PLATFORMS.length;
  const connectedCount = integrations?.length ?? 0;

  // Story 32.8 — tenant-level stale catalog list. The key is tenant-scoped
  // (no clientId): multiple IntegrationsCard instances share ONE fetch.
  // Default staleTime is fine — the list changes at most daily via the
  // 31-24 catalog-refresh cron.
  const { data: staleResources } = useQuery<StaleGrantResource[]>({
    queryKey: ["stale-grant-resources"],
    queryFn: async () => {
      const res = await apiGet("/api/integrations/stale-resources");
      if (!res.ok) return [];
      const json = (await res.json()) as {
        data: { staleResources: StaleGrantResource[] };
      };
      return json.data.staleResources;
    },
    enabled: canManage,
  });
  const hasStaleResources = (staleResources?.length ?? 0) > 0;

  // (Story 32-3: useIntegrationConnect moved above handleReauthorizeConfirm
  // so the popup-flow arm can call openCatalogPicker without a TDZ.)

  // Keep unused-but-load-bearing references to avoid bit-rot during the redesign
  void deriveFallbackHealth;
  void PageSpeedScoreBadge;

  return (
    <>
    <Card className="border-0 bg-transparent rounded-none shadow-none">
      <CardHeader className="px-0 pt-0 flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* Search bar */}
          <div className="relative w-full sm:w-[300px]">
            <MagnifyingGlass
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search integrations…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-11 rounded-2xl bg-background pl-9 pr-3 py-2 text-[13.5px] text-foreground placeholder:text-foreground outline-none focus:ring-2 focus:ring-brand/20 shadow-xs"
              style={{ border: "0.5px solid var(--hairline)" }}
            />
          </div>

          {/* Filters popover */}
          <div className="relative">
            <Button
              variant="outline"
              type="button"
              className="shrink-0 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
              style={{ border: "0.5px solid var(--hairline)" }}
              onClick={() => {
                setFilterOpen(!filterOpen);
                setFilterSearch("");
              }}
            >
              <Funnel size={16} className="mr-1" />
              Filters
              {selectedCategories.size > 0 && (
                <span className="ml-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {selectedCategories.size}
                </span>
              )}
              <CaretDown size={12} className="ml-1" />
            </Button>

            <AnimatePresence>
              {filterOpen && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => {
                      setFilterOpen(false);
                      setFilterSearch("");
                    }}
                  />

                  {/* Dropdown panel */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -4 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                    className="absolute right-0 top-full mt-2.5 z-50 w-72 bg-popover rounded-2xl shadow-md p-2"
                    style={{ border: "0.5px solid var(--hairline)" }}
                  >
                    {/* Arrow pointing up */}
                    <div
                      className="absolute -top-[5px] right-6 w-2.5 h-2.5 bg-popover rotate-45 z-10"
                      style={{
                        borderLeft: "0.5px solid var(--hairline)",
                        borderTop: "0.5px solid var(--hairline)",
                      }}
                    />

                    {/* Header label (like "Industry" / "Tags" tab on clients page) */}
                    <div className="px-4 pt-2 pb-1">
                      <span className="text-[15px] font-semibold text-foreground">Category</span>
                    </div>

                    {/* Options list */}
                    <div className="max-h-[260px] overflow-y-auto pt-2 pb-1">
                      {visibleCategoryOptions.length === 0 ? (
                        <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                          No categories match
                        </div>
                      ) : (
                        visibleCategoryOptions.map((opt) => {
                          const cat = opt.key as Exclude<LandingCategoryKey, "all">;
                          const isSelected = selectedCategories.has(cat);
                          const OptIcon = opt.icon;
                          return (
                            <button
                              key={opt.key}
                              type="button"
                              onClick={() => toggleCategory(cat)}
                              className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                            >
                              <span
                                className={`w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0 ${
                                  isSelected ? "bg-primary border-primary text-white" : ""
                                }`}
                              >
                                {isSelected && <Check size={12} />}
                              </span>
                              <OptIcon size={16} className="mr-2 text-foreground" />
                              <span className="text-foreground">{opt.label}</span>
                            </button>
                          );
                        })
                      )}
                    </div>

                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          {/* Active toggle — show only connected integrations */}
          <Button
            variant="outline"
            type="button"
            aria-pressed={showActiveOnly}
            onClick={() => setShowActiveOnly((v) => !v)}
            className={`shrink-0 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:-translate-y-0.5 hover:shadow-sm transition-all bg-background hover:bg-background ${
              showActiveOnly ? "text-foreground" : "text-foreground"
            }`}
            style={{
              border: showActiveOnly
                ? "1px solid hsl(var(--input))"
                : "0.5px solid var(--hairline)",
            }}
          >
            <CheckCircle size={16} className="mr-1" weight={showActiveOnly ? "fill" : "regular"} />
            Active
          </Button>

          {canManage && hasTenantConnections && (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
              style={{ border: "0.5px solid hsl(var(--input))" }}
              onClick={() => setAssignModalOpen(true)}
            >
              Use Existing
            </Button>
          )}

          {/* Story 32.8 — stale-resource cleanup entry point. Appears only
              when actionable: owner/admin AND a non-empty stale list. */}
          {canManage && hasStaleResources && (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-2xl text-[13px] bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
              style={{ border: "0.5px solid var(--hairline)" }}
              onClick={() => setCleanupDialogOpen(true)}
            >
              <Broom size={16} className="mr-1" />
              Clean up stale resources
            </Button>
          )}

        </div>

        <div className="flex items-center gap-6 shrink-0 ml-auto">
          {/* Story 17.2 — per-client markup settings entry point (the single
              entry point; opens the centered MarkupSettingsDialog). */}
          <Button
            variant="outline"
            type="button"
            data-testid="markup-settings-trigger"
            className="shrink-0 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
            style={{ border: "0.5px solid var(--hairline)" }}
            onClick={() => setMarkupDialogOpen(true)}
          >
            <Percent size={16} className="mr-1" />
            Markup
          </Button>

          {/* Density switcher — single-button toggle (matches ViewSwitcher pattern) */}
          {(() => {
            const nextDensity = density === "compact" ? "comfortable" : "compact";
            const Icon = density === "compact" ? DotsNine : SquaresFour;
            const label = density === "compact" ? "Compact" : "Spacious";
            return (
              <Button
                variant="outline"
                className="shrink-0 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
                style={{ border: "0.5px solid var(--hairline)" }}
                onClick={() => setDensity(nextDensity)}
                aria-label={`Switch to ${nextDensity} grid`}
              >
                <Icon size={16} className="mr-1" />
                {label}
              </Button>
            );
          })()}

          <p className="text-[14px] font-medium shrink-0">
            <span className="text-foreground">{connectedCount}</span>
            <span className="text-foreground/65"> of </span>
            <span className="text-foreground">{totalKnown}</span>
            <span className="text-foreground/65"> connected</span>
          </p>
        </div>
      </CardHeader>

      {/* Active filter chips + Clear all */}
      {selectedCategories.size > 0 && (
        <div className="flex flex-wrap items-center gap-1 px-0 pt-3">
          {Array.from(selectedCategories).map((cat) => {
            const meta = LANDING_FILTERS.find((f) => f.key === cat);
            if (!meta) return null;
            const Icon = meta.icon;
            return (
              <span
                key={`cat-${cat}`}
                className="inline-flex items-center w-fit rounded-2xl bg-background border-[0.5px] border-input px-4 text-[13px] h-10 font-medium text-foreground whitespace-nowrap"
              >
                <Icon size={14} className="shrink-0 text-foreground mr-1.5" />
                {meta.label}
                <button
                  type="button"
                  onClick={() => toggleCategory(cat)}
                  className="ml-2.5 text-foreground hover:text-destructive transition-colors leading-none flex items-center shrink-0 cursor-pointer"
                  style={{ minWidth: 0, minHeight: 0 }}
                >
                  <X size={14} />
                </button>
              </span>
            );
          })}
          <button
            type="button"
            onClick={() => setSelectedCategories(new Set())}
            className="text-[13px] hover:text-foreground min-h-[44px] px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
            style={{ color: "#5A7396" }}
          >
            Clear all
          </button>
        </div>
      )}

      <CardContent className="px-0 pt-6">
        {/* Story 32.1 — the grants → resources management surface (AC #10).
            Renders only when the client has ≥1 integration; the tile grid
            below stays the "connect a new platform" affordance. */}
        {hasIntegrations && (
          <ConnectionsTree
            clientId={clientId}
            canManage={canManage}
            showToast={showToast}
            onDisconnectResource={(target) => setDisconnectTarget(target)}
            onDiscardIncompleteSetup={(target) =>
              setDiscardIncompleteSetupTarget(target)
            }
            // Story 32.2 — "Add another grant" launches a force-fresh OAuth
            // flow that never reuses/overwrites the existing grant.
            onAddGrant={(platform) => connect(platform, { forceNewGrant: true })}
            connectingPlatform={connectingPlatform}
          />
        )}

        {/* Integration grid (landing-page-style hairlines: between-cells only, no outer box) */}
        <div
          className={
            density === "compact"
              ? "grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-10"
              : "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
          }
        >
          {visiblePlatforms.map((p, index) => {
            const totalCols = density === "compact" ? 10 : 6;
            const col = index % totalCols;
            const row = Math.floor(index / totalCols);
            const totalRows = Math.ceil(visiblePlatforms.length / totalCols);
            const isLastCol = col === totalCols - 1;
            const isLastRow = row === totalRows - 1;
            const isConnected = connectedPlatformTypes.has(p.platform);
            const isComingSoon = !p.isActive;
            const isThisConnecting = connectingPlatform === p.platform;
            const isClickable = canManage && !isComingSoon && !connectingPlatform;
            return (
              <button
                key={p.platform}
                type="button"
                onClick={() => {
                  if (!isClickable) return;
                  if (isConnected) {
                    // Story 32.1 AC #10 — the tree is the management surface:
                    // a connected tile scrolls/focuses the platform's section
                    // instead of opening the disconnect dialog directly.
                    const section = document.getElementById(
                      connectionsPlatformSectionId(p.platform)
                    );
                    if (section) {
                      section.scrollIntoView({ behavior: "smooth", block: "start" });
                      section.focus({ preventScroll: true });
                    } else {
                      // Review F2 — graceful degradation: the tree section can
                      // be absent (connections query still in flight, or its
                      // silent-empty error posture). Never dead-end a click on
                      // a connected tile — fall back to the disconnect dialog.
                      const connectedIntegration = (integrations ?? []).find(
                        (i) => i.type === p.platform
                      );
                      if (connectedIntegration) {
                        setDisconnectTarget({
                          id: connectedIntegration.id,
                          platform: connectedIntegration.type,
                          accountName: connectedIntegration.accountName,
                        });
                      }
                    }
                  } else {
                    connect(p.platform);
                  }
                }}
                disabled={!isClickable}
                title={
                  isComingSoon
                    ? `${p.displayName} — Coming soon`
                    : isConnected
                      ? `${p.displayName} — Connected (click to manage in Connections)`
                      : `Connect ${p.displayName}`
                }
                className={`group relative flex flex-col items-center justify-center gap-4 px-4 py-6 transition-colors ${
                  isClickable ? "cursor-pointer hover:bg-muted/20" : "cursor-default"
                } ${isComingSoon ? "opacity-60" : ""}`}
                style={{
                  borderRight: isLastCol ? undefined : "0.5px solid hsl(var(--input) / 0.6)",
                  borderBottom: isLastRow ? undefined : "0.5px solid hsl(var(--input) / 0.6)",
                }}
              >
                {/* Connected check badge — top-right corner */}
                {isConnected && (
                  <span
                    aria-hidden
                    className="absolute top-1.5 right-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-50/40 text-green-600"
                    style={{ border: "1px solid rgb(22 163 74)" }}
                  >
                    <Check size={12} weight="bold" />
                  </span>
                )}

                {/* Connecting spinner — top-right corner */}
                {isThisConnecting && (
                  <span
                    aria-hidden
                    className="absolute top-1.5 right-1.5 text-muted-foreground"
                  >
                    <CircleNotch size={12} weight="bold" className="animate-spin" />
                  </span>
                )}

                <img
                  src={logoUrl(p.domain)}
                  alt={p.displayName}
                  width={23}
                  height={23}
                  className={`shrink-0 rounded-sm transition-opacity ${
                    isConnected ? "" : "opacity-70 group-hover:opacity-100"
                  }`}
                  loading="lazy"
                />
                <span className="text-[13px] font-medium text-foreground/85 text-center leading-tight line-clamp-2 min-h-[2.2em]">
                  {p.displayName}
                </span>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
    {connectDialogs}
    {propertySelectorIntegrationId && (
      <PropertySelector
        open={!!propertySelectorIntegrationId}
        onOpenChange={(open) => {
          if (!open) {
            if (propertySelectionQueue.length > 0) {
              handlePropertySelectedFromQueue();
            } else {
              setPropertySelectorIntegrationId(null);
              setPropertySelectorPlatform(null);
            }
          }
        }}
        integrationId={propertySelectorIntegrationId}
        showToast={showToast}
        onPropertySelected={() => {
          refreshAfterPropertySelected();
          if (propertySelectionQueue.length > 0) {
            handlePropertySelectedFromQueue();
          } else {
            setPropertySelectorIntegrationId(null);
            setPropertySelectorPlatform(null);
          }
        }}
        {...(propertySelectorPlatform
          ? PROPERTY_SELECTOR_CONFIG[propertySelectorPlatform]
          : {})}
      />
    )}
    <PageSpeedDetailModal
      open={pageSpeedDetailOpen}
      onOpenChange={(isOpen) => {
        setPageSpeedDetailOpen(isOpen);
        if (!isOpen) setPageSpeedDetailIntegrationId(null);
      }}
      url={currentPageSpeedDetail?.url ?? ""}
      hostname={currentPageSpeedDetail?.hostname ?? null}
      metrics={currentPageSpeedDetail?.metrics ?? []}
      lastSyncAt={currentPageSpeedDetail?.lastSyncAt ?? null}
    />
    <AssignIntegrationsModal
      open={assignModalOpen}
      onOpenChange={setAssignModalOpen}
      clientId={clientId}
      clientName={clientName ?? "this client"}
      connections={tenantConnections ?? []}
      connectedPlatforms={(integrations ?? []).map((i) => i.type)}
      showToast={showToast}
      onAssigned={handleAssigned}
    />
    <ReauthorizeDialog
      open={!!reauthorizeIntegration}
      onOpenChange={(open) => {
        if (!open) setReauthorizeIntegration(null);
      }}
      accountName={reauthorizeIntegration?.accountName ?? null}
      platform={reauthorizeIntegration?.platform ?? ""}
      onConfirm={handleReauthorizeConfirm}
      loading={reauthorizeLoading}
    />
    {disconnectTarget && (
      <DisconnectIntegrationDialog
        open={!!disconnectTarget}
        onOpenChange={(open) => {
          if (!open) setDisconnectTarget(null);
        }}
        integrationId={disconnectTarget.id}
        platformName={
          PLATFORM_DISPLAY_NAME.get(disconnectTarget.platform) ?? disconnectTarget.platform
        }
        accountName={disconnectTarget.accountName}
        clientName={clientName ?? "this client"}
        showToast={showToast}
        onDisconnected={handleDisconnected}
      />
    )}
    {lastDiscardIncompleteSetupTarget?.clientId === clientId && (
      <DiscardIncompleteSetupDialog
        open={discardIncompleteSetupTarget?.clientId === clientId}
        onOpenChange={(open) => {
          if (!open && !discardingIncompleteSetupRef.current) {
            setDiscardIncompleteSetupTarget(null);
          }
        }}
        target={lastDiscardIncompleteSetupTarget}
        isPending={discardingIncompleteSetup}
        onConfirm={handleDiscardIncompleteSetup}
      />
    )}
    {/* Story 32.8 — always-mounted (directive §4.6) */}
    <CleanupStaleResourcesDialog
      open={cleanupDialogOpen}
      onOpenChange={setCleanupDialogOpen}
      resources={staleResources ?? []}
      showToast={showToast}
    />
    {/* Story 17.2 — per-client markup settings (always-mounted, directive §4.6) */}
    <MarkupSettingsDialog
      open={markupDialogOpen}
      onOpenChange={setMarkupDialogOpen}
      clientId={clientId}
      clientName={clientName}
      showToast={showToast}
    />
    <DisconnectDialog
      open={!!lastConnectionDialog}
      onOpenChange={(open) => {
        if (!open) setLastConnectionDialog(null);
      }}
      platformName={lastConnectionDialog?.platformName ?? ""}
      affectedServices={lastConnectionDialog?.affectedServices ?? []}
      onKeepAuth={handleKeepAuth}
      onRevokeAll={handleRevokeAll}
      isLoading={lastConnectionLoading}
    />
    </>
  );
}
